import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalAuth } from '@/features/auth/authContext';
import { useCanListen } from './useCanListen';
import { ListenDialog } from './ListenDialog';

/**
 * SLICE 86 — the Listen control (PLAN §4). A small "Listen" button that opens the
 * confirm sheet.
 *
 * 🔴 RENDERS NOTHING unless `micFitted && role ∈ {owner, manager}` (see
 * `useCanListen`): absence over disabled (CLAUDE.md). It is NOT writable-gated —
 * Listen is allowed while suspended (PLAN E1).
 *
 * `variant` adapts the chrome to its two homes:
 *   - `banner` — inside the red SOS banner row, so it borrows the on-SOS palette
 *     to stay legible on red (the control itself is still a Raad capability, not
 *     an SOS element — it only needs to read on that background).
 *   - `panel` — on the vehicle detail page, the ordinary Raad Blue button.
 */
export function ListenControl({
  vehicleId,
  vehicleName,
  micFitted,
  variant = 'panel',
}: {
  vehicleId: string;
  vehicleName: string;
  micFitted: boolean;
  variant?: 'banner' | 'panel';
}) {
  const { t } = useTranslation();
  const can = useCanListen(micFitted);
  const auth = useOptionalAuth();
  const [open, setOpen] = useState(false);

  if (!can) return null;

  const loginPhone =
    auth?.state.status === 'authenticated' ? (auth.state.user.phone ?? null) : null;

  const className =
    variant === 'banner'
      ? 'inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-raad-onsos/50 px-3 text-[13px] font-semibold text-raad-onsos transition-colors hover:bg-raad-onsos/15'
      : 'inline-flex min-h-11 items-center gap-2 rounded-xl bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent';

  return (
    <>
      <button
        type="button"
        data-testid="listen-button"
        onClick={() => setOpen(true)}
        className={className}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          {/* A microphone glyph — the capability is "open the mic". */}
          <rect x="9" y="2" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0M12 17v4" />
        </svg>
        {t('listen.button')}
      </button>
      {open ? (
        <ListenDialog
          vehicleId={vehicleId}
          vehicleName={vehicleName}
          loginPhone={loginPhone}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
